import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Building2, Palette, ShieldCheck, KeyRound } from "lucide-react";

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b ek-line last:border-0">
    <span className="text-[11px] tracking-[0.14em] uppercase ek-text-muted pt-0.5">{label}</span>
    <span className="text-sm ek-text text-right">{children}</span>
  </div>
);

export default function InstanceBrandingPanel() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/instance/config").then(({ data }) => setCfg(data)).catch(() => setErr(true));
  }, []);

  if (err) return <div className="text-sm text-rose-600">Could not load instance config.</div>;
  if (!cfg) return <div className="ek-text-muted">Loading…</div>;

  const b = cfg.branding || {};
  return (
    <div data-testid="instance-branding-panel" className="max-w-2xl space-y-6">
      <div>
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7737F6] mb-2">
          <Building2 size={13} className="inline mr-1" /> Instance &amp; branding
        </div>
        <h2 className="text-2xl font-semibold ek-text">Which white-label instance is this?</h2>
        <p className="text-sm ek-text-muted mt-1">Read-only — set at deploy time via per-instance environment variables. Confirm at a glance before generating client deliverables.</p>
      </div>

      <div className="border ek-line rounded-lg p-5">
        <Row label="Instance ID"><code data-testid="ib-instance-id" className="text-[13px] ek-surface-2 px-2 py-0.5 rounded">{cfg.instance_id}</code></Row>
        <Row label="Name"><span data-testid="ib-name">{cfg.name}</span></Row>
        <Row label="Tenancy">{cfg.single_tenant ? "Single-tenant (dedicated)" : "Shared"}</Row>
        <Row label="Primary colour">
          <span className="inline-flex items-center gap-2">
            <Palette size={14} className="ek-text-muted" />
            <span data-testid="ib-primary" className="font-mono text-[13px]">{b.primary_color}</span>
            <span className="inline-block w-5 h-5 rounded border ek-line" style={{ backgroundColor: b.primary_color }} />
          </span>
        </Row>
        <Row label="Logo">
          {b.logo_url
            ? <img src={b.logo_url} alt={cfg.name} style={{ height: 28, width: "auto" }} className="inline-block" />
            : <span className="ek-text-muted">Default Ekselens mark</span>}
        </Row>
        <Row label="Attribution"><span data-testid="ib-attribution">{b.attribution}</span></Row>
        <Row label="Owner"><span>{cfg.owner_email || <span className="ek-text-muted">—</span>}</span></Row>
      </div>

      <div>
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7737F6] mb-2">
          <KeyRound size={13} className="inline mr-1" /> Entitlements
        </div>
        <div className="flex flex-wrap gap-2" data-testid="ib-entitlements">
          {(cfg.entitlements || []).map((e) => (
            <span key={e} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1">
              <ShieldCheck size={12} /> {e}
            </span>
          ))}
          {(cfg.entitlements || []).length === 0 && <span className="text-sm ek-text-muted">No features entitled.</span>}
        </div>
      </div>
    </div>
  );
}
